import { Alert, Checkbox, Empty, Form, Input, Radio, Select, Space, Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import dayjs, { type Dayjs } from "dayjs";
import { DatePicker } from "../../../../../shared/frontend/components/ControlledPickers";
import { formatDateOnlyIso } from "../app/utils/formatDateOnlyIso";
import { REMINDER_ACTION_RETRY } from "../app/utils/reminderAction";
import { spacing } from "../app/design-system";
import { useQuery } from "@tanstack/react-query";
import { globalKey } from "../app/query/queryKeys";
import { useAuth } from "../app/auth/useAuth";
import { useI18n } from "../app/i18n/useI18n";
import { formatDateOnly, formatMoney } from "../app/units/format";
import { loadUnits } from "../app/units/unitsStorage";
import {
  DeliveryChannelSection,
  toBackendDeliveryChannel,
  type DeliveryChannel
} from "./communication/DeliveryChannelSection";
import { FormDrawer } from "./FormDrawer";
import { useFormDirty } from "../../../../../shared/frontend/forms/useFormDirty";
import { formSnapshot } from "../../../../../shared/frontend/forms/formSnapshot";
import {
  initialReminderChannel,
  loadReminderAssistantData,
  reminderAssistantQueryParts,
  type LetterSendingIntegrationDto,
  type ReminderAssistantOpenItem
} from "./reminderAssistantData";
import { useFeedback } from "../../../../../shared/frontend/feedback/useFeedback";
import { ReminderPrintPreview } from "./ReminderPrintPreview";

import { CHANNEL_LETTER_LC } from "../app/constants/communicationChannel";

/** Kanalwert der Inkasso-Übergabe in Mahnungen. */
const CHANNEL_DEBT_COLLECTION = "debt_collection";

export type ReminderCaseTrigger = {
  triggerOpenItemId: string;
  openItemIds?: string[];
  preferredOpenItemIds?: string[];
};

type ReminderCreateFormValues = {
  collectionDate?: Dayjs;
  reminderLevelId: string;
  channel: string;
  /** Textvariante der Mahnung; leer = ausgelieferter Grundfall. */
  variantId?: string;
  inkassoMode?: "immediate" | "prepare";
  externalReference?: string | null;
  notes?: string | null;
  // Meldung an den Inkasso-Dienstleister (eigene Variante und Versandart)
  debtCollectionVariantId?: string;
  debtCollectionChannel?: string;
};

export type ReminderCreateDrawerProps = {
  open: boolean;
  personId: string;
  triggerOpenItemId: string;
  initialSelectedOpenItemIds?: string[];
  ignoreReminderDelay?: boolean;
  onCancel: () => void;
  onCreated: () => void;
};

export function ReminderCreateDrawer({
  open,
  personId,
  triggerOpenItemId,
  initialSelectedOpenItemIds,
  ignoreReminderDelay = false,
  onCancel,
  onCreated
}: ReminderCreateDrawerProps) {
  const auth = useAuth();
  const feedback = useFeedback();
  const { text } = useI18n();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const [form] = Form.useForm<ReminderCreateFormValues>();
  const { dirty, initialize, resetDirty } = useFormDirty(form);
  // Merker, für welchen Fall das Formular schon vorbelegt wurde. Bewusst eine Ref: der Wert steuert
  // keine Anzeige, und als Zustand löste er bei jedem Öffnen einen zusätzlichen Renderdurchlauf aus.
  const seededCaseKeyRef = React.useRef<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [createdPrintReminder, setCreatedPrintReminder] = React.useState<{ id: string; personId: string } | null>(null);

  const enabled = open && !!personId && !!triggerOpenItemId;
  /**
   * Ausgewählte Positionen; `null` = der ganze Vorgang. Der Vorgang umfasst standardmäßig alles
   * Fällige der Person im Studio — wer etwas abwählt, hat dafür einen Grund, etwa eine laufende
   * Klärung zu genau diesem Vertrag.
   */
  const [selectedOpenItemIds, setSelectedOpenItemIds] = React.useState<string[] | null>(
    () => initialSelectedOpenItemIds?.length ? initialSelectedOpenItemIds : null
  );
  const request = React.useMemo(
    () => ({
      personId,
      openItemId: triggerOpenItemId,
      ignoreReminderDelay,
      selectedOpenItemIds: selectedOpenItemIds ?? undefined
    }),
    [ignoreReminderDelay, personId, selectedOpenItemIds, triggerOpenItemId]
  );
  const assistantQuery = useQuery({
    queryKey: globalKey("reminders", "assistant", reminderAssistantQueryParts(request)),
    queryFn: () => loadReminderAssistantData(auth.api, request),
    enabled,
    // Ein Mahnfall ändert sich zwischen zwei Öffnungen; ein Cache-Treffer würde einen inzwischen
    // erledigten Posten weiter anbieten.
    staleTime: 0,
    retry: false
  });

  const data = enabled ? (assistantQuery.data ?? null) : null;
  const context = data?.context ?? null;

  /**
   * Der volle Vorgang — die Zeilen, die zur Auswahl stehen. Wird eine Position abgewählt, liefert
   * die Hauptabfrage nur noch die verbliebenen; die abgewählte Zeile muss aber sichtbar bleiben,
   * sonst ließe sich die Abwahl nicht rückgängig machen.
   *
   * Ohne Auswahl ist der Abfrageschlüssel identisch mit dem der Hauptabfrage — React Query fasst
   * beide dann zu einer Anfrage zusammen. Eine zweite Anfrage entsteht erst, sobald tatsächlich
   * abgewählt wurde.
   */
  const fullRequest = React.useMemo(
    () => ({ personId, openItemId: triggerOpenItemId, ignoreReminderDelay }),
    [ignoreReminderDelay, personId, triggerOpenItemId]
  );
  const fullCaseQuery = useQuery({
    queryKey: globalKey("reminders", "assistant", reminderAssistantQueryParts(fullRequest)),
    queryFn: () => loadReminderAssistantData(auth.api, fullRequest),
    enabled,
    staleTime: 0,
    retry: false
  });
  const allOpenItems: ReminderAssistantOpenItem[] = fullCaseQuery.data?.context.openItems ?? [];

  const isSelected = React.useCallback(
    (openItemId: string) => selectedOpenItemIds === null || selectedOpenItemIds.includes(openItemId),
    [selectedOpenItemIds]
  );
  const toggleOpenItem = React.useCallback(
    (openItemId: string, checked: boolean) => {
      setSelectedOpenItemIds((current) => {
        const base = current ?? allOpenItems.map((item) => item.openItemId);
        const next = checked ? [...new Set([...base, openItemId])] : base.filter((id) => id !== openItemId);
        // Der auslösende Posten bleibt immer Teil des Vorgangs — ohne ihn gäbe es keinen Anlass.
        if (!next.includes(triggerOpenItemId)) next.push(triggerOpenItemId);
        return next.length === allOpenItems.length ? null : next;
      });
    },
    [allOpenItems, triggerOpenItemId]
  );
  const allowedChannels = data?.allowedChannels ?? [];
  const channelUnavailableReasonKey = data?.channelUnavailableReasonKey ?? null;
  const debtCollectionAgencyId = data?.debtCollectionAgencyId ?? null;
  const letterSendingIntegrations: LetterSendingIntegrationDto[] = data?.letterSendingIntegrations ?? [];
  // Nur der Erstaufbau blendet den Inhalt aus. Beim Ab- oder Anwählen einer Position wird neu
  // aufgelöst — dabei darf das Formular nicht verschwinden, sonst springt die Maske bei jedem Klick.
  const loading = enabled && assistantQuery.isPending;

  const watchedChannel = Form.useWatch("channel", form);
  const isDebtCollection = watchedChannel === CHANNEL_DEBT_COLLECTION && Boolean(debtCollectionAgencyId);

  /**
   * Die Textvarianten der Mahnung. Ohne Auswahl gilt der Grundfall der Dokumentart — der Aufbau
   * des Schreibens steht im System, die Variante gibt ihr nur den Ton.
   */
  const [reminderVariants, setReminderVariants] = React.useState<Array<{ id: string; name: string }>>([]);
  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const query = new URLSearchParams({ language: "de" });
    if (context?.studioId) query.set("studioId", context.studioId);
    auth.api
      .get<Array<{ id: string; name: string }>>(`/api/v1/system-documents/payment-reminder/variants?${query.toString()}`)
      .then((list) => { if (!cancelled) setReminderVariants(list || []); })
      .catch(() => { if (!cancelled) setReminderVariants([]); });
    return () => { cancelled = true; };
  }, [auth.api, context?.studioId, enabled]);

  // Varianten der Inkasso-Meldung — erst laden, wenn die Übergabe tatsächlich ansteht.
  const [debtCollectionVariants, setDebtCollectionVariants] = React.useState<Array<{ id: string; name: string }>>([]);
  React.useEffect(() => {
    if (!isDebtCollection) return;
    let cancelled = false;
    auth.api
      .get<Array<{ id: string; name: string }>>("/api/v1/system-documents/debt-collection-handover/variants?language=de")
      .then((list) => { if (!cancelled) setDebtCollectionVariants(list || []); })
      .catch(() => { if (!cancelled) setDebtCollectionVariants([]); });
    return () => { cancelled = true; };
  }, [auth.api, isDebtCollection]);

  React.useEffect(() => {
    if (!enabled) {
      form.resetFields();
      resetDirty();
      seededCaseKeyRef.current = null;
    }
  }, [enabled, form, resetDirty]);

  // Die Auswahl gilt für genau diesen Vorgang und wird beim Verlassen verworfen — im Handler, nicht
  // in einem Effekt: Der Drawer bleibt montiert, ein Zustandswechsel im Effekt löste nur eine
  // zusätzliche Renderrunde aus.
  const closeAndReset = React.useCallback((close: () => void) => {
    setSelectedOpenItemIds(initialSelectedOpenItemIds?.length ? initialSelectedOpenItemIds : null);
    close();
  }, [initialSelectedOpenItemIds]);

  // antd-Formulare sind imperativ. Die Vorbelegung läuft **einmal je Fall**, nicht bei jedem
  // Render: sonst überschriebe ein Neuzeichnen die Eingabe des Sachbearbeiters.
  React.useEffect(() => {
    if (!data) return;
    const key = `${request.personId}|${request.openItemId}|${request.ignoreReminderDelay}`;
    if (seededCaseKeyRef.current === key) return;
    seededCaseKeyRef.current = key;
    initialize({
      reminderLevelId: data.context.reminderLevelId,
      collectionDate: data.context.suggestedCollectionDate ? dayjs(data.context.suggestedCollectionDate) : undefined,
      channel: initialReminderChannel(data),
      variantId: data.suggestedVariantId,
      inkassoMode: data.debtCollectionAgencyId ? "immediate" : undefined,
      externalReference: "",
      notes: ""
    });
  }, [data, form, request, initialize]);

  // Die Stufe des Schreibens bestimmt der Server und sie hängt an der Auswahl: Fällt die am
  // weitesten fortgeschrittene Kette heraus, sinkt sie. Deshalb wird sie bei jeder Antwort
  // nachgezogen und nicht nur beim ersten Aufbau des Formulars.
  React.useEffect(() => {
    if (!context) return;
    form.setFieldValue("reminderLevelId", context.reminderLevelId);
  }, [context, form]);

  const submitReminder = React.useCallback(async (values: ReminderCreateFormValues) => {
    if (!context || createdPrintReminder) return;
    setSubmitting(true);
    try {
      const created = await auth.api.post<{ id: string; personId: string; channel: string }>("/api/v1/reminders", {
        personId: context.personId,
        collectionDate: context.action === REMINDER_ACTION_RETRY ? formatDateOnlyIso(values.collectionDate) : null,
        openItemId: context.openItemId || triggerOpenItemId,
        reminderLevelId: values.reminderLevelId,
        channel: toBackendDeliveryChannel(values.channel as DeliveryChannel),
        variantId: values.variantId || null,
        studioId: context.studioId || null,
        externalReference: values.externalReference?.trim() || null,
        notes: values.notes?.trim() || null,
        prepareOnly: isDebtCollection && values.inkassoMode === "prepare",
        // Die Auswahl geht mit: Der Server mahnt genau das, was der Assistent gezeigt hat.
        openItemIds: selectedOpenItemIds,
        ignoreReminderDelay,
        // Meldung an den Dienstleister
        debtCollectionVariantId: isDebtCollection ? values.debtCollectionVariantId || null : null,
        debtCollectionSendEmail: isDebtCollection ? values.debtCollectionChannel !== CHANNEL_LETTER_LC : null
      });
      feedback.success(text("Reminder created."));
      if (created.channel === CHANNEL_LETTER_LC) {
        // Die Anlage ist abgeschlossen. Vorschau und Druck arbeiten ab jetzt mit genau
        // diesem Beleg; ein abgebrochener Druck darf keine erneute Mahnung erzeugen.
        resetDirty();
        setCreatedPrintReminder({ id: created.id, personId: created.personId });
        return;
      }
      closeAndReset(onCreated);
    } finally {
      setSubmitting(false);
    }
  }, [auth.api, closeAndReset, context, createdPrintReminder, ignoreReminderDelay, isDebtCollection, feedback, onCreated, resetDirty, selectedOpenItemIds, text, triggerOpenItemId]);

  const create = async () => {
    if (!context) return;
    const values = await form.validateFields();
    await submitReminder(values);
  };

  if (createdPrintReminder) return <ReminderPrintPreview reminderId={createdPrintReminder.id} personId={createdPrintReminder.personId}
    onClose={() => {
      setCreatedPrintReminder(null);
      closeAndReset(onCreated);
    }} />;

  return (
    <FormDrawer mode="edit"
      dirty={dirty || formSnapshot([...(selectedOpenItemIds ?? allOpenItems.map(item => item.openItemId))].sort()) !== formSnapshot([...(initialSelectedOpenItemIds?.length ? initialSelectedOpenItemIds : allOpenItems.map(item => item.openItemId))].sort())}
      title="Create reminder"
      open={open}
      onCancel={() => closeAndReset(onCancel)}
      formId="reminder-create-drawer-form"
      submitting={submitting}
      contentLoading={loading}
      submitText="Create"
      submitDisabled={loading || !context || allowedChannels.length === 0 || !!context.collectionUnavailableReasonKey}
    >
      {context ? (
        <Form
          id="reminder-create-drawer-form"
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={() => create().catch(() => {})}
        >
          <Form.Item label={text("Person")}>
            <Input value={context.personName} disabled />
          </Form.Item>
          <Form.Item label={text("Studio")}>
            <Input value={context.studioName || text("—")} disabled />
          </Form.Item>
          <Form.Item name="reminderLevelId" label={text("Reminder level")} rules={[{ required: true }]}>
            <Select options={[{ value: context.reminderLevelId, label: context.reminderLevelName }]} disabled />
          </Form.Item>
          <Form.Item label={text("Reminder reason")}>
            <Input value={context.reminderReason === "mixed" ? text("Mixed reminder case") : context.reminderReason === "chargeback" ? text("Chargeback") : text("Regular open item")} disabled />
          </Form.Item>
          {context.action === REMINDER_ACTION_RETRY && (
            <>
              {context.collectionUnavailableReasonKey && <Alert type="warning" showIcon title={text(context.collectionUnavailableReasonKey)} />}
              <Form.Item name="collectionDate" label={text("Retry collection date")} rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} disabledDate={(date) => !context.suggestedCollectionDate
                  || date.isBefore(dayjs(context.suggestedCollectionDate), "day")} />
              </Form.Item>
            </>
          )}
          {channelUnavailableReasonKey ? (
            <Alert
              type="warning"
              showIcon
              title={text(channelUnavailableReasonKey)}
              style={{ marginBottom: spacing.lg }}
            />
          ) : null}
          <DeliveryChannelSection
            open={open}
            form={form}
            studioId={context.studioId}
            personEmail={context.personEmail}
            integrations={letterSendingIntegrations}
            variantOptions={reminderVariants.map((variant) => ({ value: variant.id, label: variant.name }))}
            variantLabel={text("Reminder text variant")}
            variantFieldName="variantId"
            channelFieldName="channel"
            allowedChannels={allowedChannels as DeliveryChannel[]}
            lockedChannel={isDebtCollection ? "debt_collection" : undefined}
            showVariant={!isDebtCollection}
          />
          {isDebtCollection ? (
            <>
              <Form.Item name="inkassoMode" label={text("Debt collection handover mode")}>
                <Radio.Group>
                  <Radio value="immediate">{text("Debt collection handover immediate")}</Radio>
                  <Radio value="prepare">{text("Debt collection handover prepare")}</Radio>
                </Radio.Group>
              </Form.Item>
              {/*
                Übergabeschreiben an den Dienstleister — nicht an das Mitglied. Bewusst dieselbe
                Komponente wie beim Mahnversand, nur mit eigenen Feldnamen und den Vorlagen der
                Briefart „Inkasso-Übergabe".
              */}
              <Typography.Text type="secondary">{text("Debt collection handover letter hint")}</Typography.Text>
              <DeliveryChannelSection
                open={open}
                form={form}
                studioId={context.studioId}
                integrations={letterSendingIntegrations}
                variantOptions={debtCollectionVariants.map((variant) => ({ value: variant.id, label: variant.name }))}
                variantLabel={text("Debt collection handover text variant")}
                variantFieldName="debtCollectionVariantId"
                channelFieldName="debtCollectionChannel"
                allowedChannels={["email", "letter"] as DeliveryChannel[]}
                validateEmailTemplates={false}
              />
            </>
          ) : null}
          <Form.Item label={text("Open items")}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {allOpenItems.map((openItem) => (
                <div key={openItem.openItemId} style={{ display: "flex", justifyContent: "space-between", gap: spacing.lg }}>
                  <Checkbox
                    checked={isSelected(openItem.openItemId)}
                    disabled={openItem.openItemId === triggerOpenItemId}
                    onChange={(event) => toggleOpenItem(openItem.openItemId, event.target.checked)}
                  >
                    {openItem.description} ({formatDateOnly(openItem.dueDate, units)})
                    {openItem.contractName ? (
                      <Typography.Text type="secondary"> · {openItem.contractName}</Typography.Text>
                    ) : null}
                    {/* Die Stufe steht nur dort, wo sie von der des Schreibens abweicht — sonst
                        wiederholte jede Zeile den Briefkopf. */}
                    {openItem.targetReminderLevel !== context.reminderLevelLevel ? (
                      <Typography.Text type="secondary"> · {openItem.targetReminderLevelName}</Typography.Text>
                    ) : null}
                    {openItem.reason === "chargeback" ? (
                      <Typography.Text type="secondary"> · {text("Chargeback")}</Typography.Text>
                    ) : openItem.reason === "chargebackFee" ? (
                      <Typography.Text type="secondary"> · {text("Chargeback fee")}</Typography.Text>
                    ) : null}
                  </Checkbox>
                  <Typography.Text
                    strong
                    type={isSelected(openItem.openItemId) ? undefined : "secondary"}
                    delete={!isSelected(openItem.openItemId)}
                  >
                    {formatMoney(Number(openItem.openAmount || 0), units)}
                  </Typography.Text>
                </div>
              ))}
              {context.reminderFeeAmount > 0 ? (
                <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.lg }}>
                  <Typography.Text>{text("Reminder fee")} ({context.reminderLevelName})</Typography.Text>
                  <Typography.Text strong>{formatMoney(context.reminderFeeAmount, units)}</Typography.Text>
                </div>
              ) : null}
              <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.lg }}>
                <Typography.Text strong>{text("Total")}</Typography.Text>
                <Typography.Text strong>
                  {formatMoney(Number(context.totalAmount || 0), units)}
                </Typography.Text>
              </div>
            </Space>
          </Form.Item>
          <Form.Item name="externalReference" label={text("External reference (optional)")}>
            <Input allowClear placeholder={text("Optional")} />
          </Form.Item>
          <Form.Item name="notes" label={text("Notes (optional)")}>
            <Input.TextArea rows={2} allowClear placeholder={text("Optional")} />
          </Form.Item>
        </Form>
      ) : !loading ? (
        <Empty description={text("No data")} />
      ) : null}
    </FormDrawer>
  );
}
